import {
  buildCanonicalEvent,
  deriveAcquisitionContext,
  mapCanonicalEventToGA,
} from './analytics-core.mjs'
import {
  clearAnalyticsIdentity,
  resolveAnalyticsIdentity,
} from './analytics-identity.mjs'
import { clearAnalyticsDeliveryQueue, deliverAnalyticsEvent, flushAnalyticsDeliveryQueue } from './analytics-delivery.mjs'

export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-M42S63S6X0'

export const ANALYTICS_CONSENT_EVENT = 'sb:analytics-consent-changed'
export const ANALYTICS_INTERACTION_EVENT = 'sb:analytics-interaction'

const CONSENT_COOKIE = 'CookieConsent'
const JOURNEY_COOKIE = 'sb_journey_id'
const FIRST_TOUCH_STORAGE_KEY = 'sb_analytics_first_touch_v1'
const DELIVERY_QUEUE_KEY = 'sb_analytics_delivery_queue_v1'
const DELIVERY_ENDPOINT = '/api/analytics/events'

const readCookie = (name) => {
  if (typeof document === 'undefined') return ''
  const prefix = `${encodeURIComponent(name)}=`
  return document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length) || ''
}

export const hasAnalyticsConsent = () => readCookie(CONSENT_COOKIE) === 'true'

export function announceAnalyticsInteraction(eventName, properties = {}) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(ANALYTICS_INTERACTION_EVENT, {
      detail: { eventName, properties },
    })
  )
}

const setJourneyCookie = (journeyId) => {
  if (!journeyId || typeof document === 'undefined') return
  const maxAge = 90 * 24 * 60 * 60
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${JOURNEY_COOKIE}=${encodeURIComponent(journeyId)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`
}

const removeJourneyCookie = () => {
  if (typeof document === 'undefined') return
  document.cookie = `${JOURNEY_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
}

const readFirstTouch = () => {
  try {
    const value = window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)
    return value ? JSON.parse(value) : null
  } catch {
    return null
  }
}

const persistFirstTouch = (acquisition) => {
  const firstTouch = {
    source: acquisition.first_touch_source,
    medium: acquisition.first_touch_medium,
    campaign: acquisition.first_touch_campaign,
    landing_page_url: acquisition.first_touch_landing_page_url,
  }
  try {
    const serialised = JSON.stringify(firstTouch)
    if (window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY) !== serialised) {
      window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, serialised)
    }
  } catch {
    // Analytics storage can be blocked; collection continues without persistence.
  }
}

const browserContext = () => {
  const width = window.innerWidth
  const viewportGroup = width < 600 ? 'small' : width < 1024 ? 'medium' : 'large'
  const userAgent = window.navigator.userAgent || ''
  const deviceCategory = /Mobi|Android/i.test(userAgent)
    ? 'mobile'
    : /Tablet|iPad/i.test(userAgent)
      ? 'tablet'
      : 'desktop'

  let timezoneName = ''
  try {
    timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    timezoneName = ''
  }

  return {
    browser_language: window.navigator.language || '',
    timezone_name: timezoneName,
    device_category: deviceCategory,
    viewport_group: viewportGroup,
  }
}

const acquisitionContext = () => {
  const context = deriveAcquisitionContext({
    currentUrl: window.location.href,
    referrerUrl: document.referrer,
    storedFirstTouch: readFirstTouch(),
  })
  persistFirstTouch(context)
  return context
}

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments)
  }
  return window.gtag
}

const sendFirstPartyEvent = (event) => {
  flushAnalyticsDeliveryQueue({ endpoint: DELIVERY_ENDPOINT, storage: window.localStorage, storageKey: DELIVERY_QUEUE_KEY, hasConsent: hasAnalyticsConsent }).catch(() => {})
  deliverAnalyticsEvent({ endpoint: DELIVERY_ENDPOINT, event, storage: window.localStorage, storageKey: DELIVERY_QUEUE_KEY, hasConsent: hasAnalyticsConsent }).catch(() => {})
}

export function trackGAEvent(eventName, parameters = {}) {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return
  ensureGtag()('event', eventName, parameters)
}

export function trackCanonicalEvent(eventName, { context = {}, properties = {} } = {}) {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return null

  const identity = resolveAnalyticsIdentity({ storage: window.localStorage })
  setJourneyCookie(identity.journey_id)
  const event = buildCanonicalEvent({
    eventName,
    context: {
      ...acquisitionContext(),
      ...browserContext(),
      ...context,
    },
    properties,
    identity,
    consentState: 'analytics',
  })
  if (!event) return null

  const ga = mapCanonicalEventToGA(event)
  ensureGtag()('event', ga.eventName, ga.parameters)
  sendFirstPartyEvent(event)
  return event
}

export function trackPageView(url, context = {}) {
  return trackCanonicalEvent('page_viewed', {
    context: {
      page_title: document.title,
      page_location: window.location.href,
      page_path: url,
      ...context,
    },
  })
}

export function clearAnalyticsClientState() {
  if (typeof window === 'undefined') return
  clearAnalyticsIdentity(window.localStorage)
  clearAnalyticsDeliveryQueue(window.localStorage, DELIVERY_QUEUE_KEY)
  try {
    window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY)
  } catch {
    // Ignore blocked storage.
  }
  removeJourneyCookie()
}
